// src/pages/reports/SubscriberDetailReport.tsx
import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import PageTitle from 'src/components/PageTitle';
import PageTitleWrapper from 'src/components/PageTitleWrapper';
import { Box, Typography, Paper, Button, Stack, TextField } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import DownloadIcon from '@mui/icons-material/Download';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import dayjs, { Dayjs } from 'dayjs';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { authenticationService } from 'src/services/authentication.service';
import { get } from 'src/services/httpClient.service';
import { hasPermission } from 'src/components/Permissions/hasPermission';
import reportDownloadService from 'src/services/reportDownload.service';

// Set dayjs locale to use DD/MM/YYYY format
import 'dayjs/locale/en-gb';
dayjs.locale('en-gb');

// Define columns for the report download service - UPDATED: Removed userId, added customerId
const columns = [
  { field: 'id', headerName: 'ID', width: 90 },
  { field: 'customerId', headerName: 'Customer ID', minWidth: 150 },
  { field: 'email', headerName: 'User Email', minWidth: 200 },
  { field: 'stbNo', headerName: 'Device Id', minWidth: 150 },
  { field: 'startDate', headerName: 'Subscription Start Date', minWidth: 200 },
  { field: 'endDate', headerName: 'Subscription End Date', minWidth: 200 },
  { field: 'paymentStatus', headerName: 'Payment Status', minWidth: 150 },
  { field: 'user_status', headerName: 'User Status', minWidth: 120 },
  { field: 'subscriptionPlan', headerName: 'Subscription Status', minWidth: 150 },
  { field: 'device_status', headerName: 'Device Status', minWidth: 120 },
  { field: 'location', headerName: 'Location', minWidth: 150 },
  { field: 'createdDate', headerName: 'Created Date', minWidth: 180 },
  { field: 'updatedDate', headerName: 'Updated Date', minWidth: 180 },
];

const SubscriberDetailReport = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState({ pdf: false, excel: false });
  const [pageSize, setPageSize] = useState(100);
  const [searchTerm, setSearchTerm] = useState('');
  const [startDate, setStartDate] = useState<Dayjs | null>(dayjs().startOf('month'));
  const [endDate, setEndDate] = useState<Dayjs | null>(dayjs().endOf('month'));
  const { vendorId, franchiseId } = authenticationService.getVendorFranchiseIds();

  const [subscriberStats, setSubscriberStats] = useState({
    total: 0,
    active: 0,
    suspended: 0,
  });

  useEffect(() => {
    const fetchData = async () => {
      if (!startDate || !endDate) return;

      setLoading(true);
      try {
        const from = dayjs(startDate).format('YYYY-MM-DD');
        const to = dayjs(endDate).format('YYYY-MM-DD');

        const response = await get(
          `${process.env.REACT_APP_BASE_API_URL}/api/reports/data/${vendorId}?fromDate=${from}&toDate=${to}`
        );

        const dataWithId = response.map((item: any, index: number) => ({
          id: index + 1,
          startDateStr: item.startDate ? dayjs(item.startDate).format('DD/MM/YYYY HH:mm:ss') : '',
          endDateStr: item.endDate ? dayjs(item.endDate).format('DD/MM/YYYY HH:mm:ss') : '',
          createdDate: item.createdDate ? dayjs(item.createdDate).format('DD/MM/YYYY HH:mm:ss') : '',
          updatedDate: item.updatedDate ? dayjs(item.updatedDate).format('DD/MM/YYYY HH:mm:ss') : '',
          // Ensure customerId is available, fallback to userId if needed
          customerId: item.customerId || (item.userId ? String(item.userId) : ''),
          ...item,
        }));
        setRows(dataWithId);
        
        // Calculate stats
        const active = dataWithId.filter((item: any) => item.user_status === 'ACTIVE').length;
        const suspended = dataWithId.filter((item: any) => item.user_status === 'SUSPENDED').length;
        
        setSubscriberStats({
          total: dataWithId.length,
          active,
          suspended
        });
      } catch (error) {
        console.error('Error fetching report data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [startDate, endDate, vendorId]);

  // Generate timestamped filename for Excel only
  const generateTimestampedFilename = (
    baseName: string,
    start: Dayjs | null,
    end: Dayjs | null
  ): string => {
    const from = start ? dayjs(start).format('YYYY-MM-DD') : 'NA';
    const to = end ? dayjs(end).format('YYYY-MM-DD') : 'NA';
    const time = dayjs().format('HH-mm-ss');
    return `${baseName}_${from}_to_${to}_${time}`;
  };

  // Keep your original Excel logic with loading state - UPDATED with customerId
  const handleDownloadExcel = async () => {
    if (!startDate || !endDate) {
      alert('Please select both start and end dates');
      return;
    }

    setDownloading(prev => ({ ...prev, excel: true }));
    
    try {
      const from = dayjs(startDate).format('YYYY-MM-DD');
      const to = dayjs(endDate).format('YYYY-MM-DD');

      // Prepare headers - REMOVED User Id, ADDED Customer ID
      const headers = [
        'ID',
        'Customer ID',
        'User Email',
        'Device Id',
        'Subscription Start Date',
        'Subscription End Date',
        'Payment Status',
        'User Status',
        'Subscription Status',
        'Device Status',
        'Location',
        'Created Date',
        'Updated Date'
      ];

      // Prepare data rows from filtered UI data
      const data = filteredRows.map((row: any) => [
        row.id || '',
        row.customerId || '',
        row.email || '',
        row.stbNo || '',
        row.startDateStr || '',
        row.endDateStr || '',
        row.paymentStatus || '',
        row.user_status || '',
        row.subscriptionPlan || '',
        row.device_status || '',
        row.location || '',
        row.createdDate || '',
        row.updatedDate || ''
      ]);

      // Combine headers and rows
      const finalData = [headers, ...data];

      // Create worksheet and workbook
      const worksheet = XLSX.utils.aoa_to_sheet(finalData);
      const workbook = XLSX.utils.book_new();

      // Add worksheet with a safe name
      const sheetName = 'Subscriber_Detail_Report';
      XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);

      // Generate Excel buffer and save
      const excelBuffer = XLSX.write(workbook, {
        bookType: 'xlsx',
        type: 'array',
      });

      const blob = new Blob([excelBuffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });

      const filename = generateTimestampedFilename('Subscriber_Detail_Report', startDate, endDate) + '.xlsx';
      saveAs(blob, filename);
    } catch (error) {
      console.error('Download failed:', error);
    } finally {
      setDownloading(prev => ({ ...prev, excel: false }));
    }
  };

  // Single download handler for PDF using common service - UPDATED with customerId
  const handleDownloadPDF = async () => {
    if (!startDate || !endDate) {
      alert('Please select both start and end dates');
      return;
    }

    setDownloading(prev => ({ ...prev, pdf: true }));
    
    try {
      const fromDate = startDate.format('DD/MM/YYYY');
      const toDate = endDate.format('DD/MM/YYYY');
      const fromStr = startDate.format('YYYY-MM-DD');
      const toStr = endDate.format('YYYY-MM-DD');
      const searchFilterText = searchTerm || 'No Search Filter';
      
      // Generate custom filename
      const customFileName = `Subscriber_Detail_Report_${fromStr}_to_${toStr}_${dayjs().format('HHmmss')}.pdf`;
      
      // Update PDF columns - remove userId, add customerId
      const pdfColumns = [
        { field: 'id', headerName: 'ID', width: 90 },
        { field: 'customerId', headerName: 'Customer ID', minWidth: 150 },
        { field: 'email', headerName: 'User Email', minWidth: 200 },
        { field: 'stbNo', headerName: 'Device Id', minWidth: 150 },
        { field: 'startDate', headerName: 'Subscription Start Date', minWidth: 200 },
        { field: 'endDate', headerName: 'Subscription End Date', minWidth: 200 },
        { field: 'paymentStatus', headerName: 'Payment Status', minWidth: 150 },
        { field: 'user_status', headerName: 'User Status', minWidth: 120 },
        { field: 'subscriptionPlan', headerName: 'Subscription Status', minWidth: 150 },
        { field: 'device_status', headerName: 'Device Status', minWidth: 120 },
        { field: 'location', headerName: 'Location', minWidth: 150 },
        { field: 'createdDate', headerName: 'Created Date', minWidth: 180 },
        { field: 'updatedDate', headerName: 'Updated Date', minWidth: 180 },
      ];

      const success = await reportDownloadService.downloadReport({
        title: 'Subscriber Detail Report',
        columns: pdfColumns,
        rows: filteredRows,
        month: startDate.month() + 1,
        year: startDate.year(),
        fileName: customFileName,
      }, 'pdf');
      
      if (!success) {
        alert('Failed to generate PDF. Please try again.');
      }
    } catch (error) {
      console.error('Error downloading PDF:', error);
      alert('Error generating PDF. Please try again.');
    } finally {
      setDownloading(prev => ({ ...prev, pdf: false }));
    }
  };

  const filteredRows = rows.filter((row) =>
    Object.values(row).some(
      (value) =>
        typeof value === 'string' &&
        value.toLowerCase().includes(searchTerm.toLowerCase())
    )
  );

  return (
    <>
      <Helmet>
        <title>Subscriber Detail Report</title>
      </Helmet>

      <PageTitleWrapper>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
          <Box>
            <PageTitle
              heading="Subscriber Detail Report"
              subHeading="Overview of subscriber sessions, plans, and access logs"
              handleClick={undefined}
            />
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {hasPermission(["OWNER"]) && (
              <>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<DownloadIcon />}
                  onClick={handleDownloadExcel}
                  disabled={downloading.excel}
                >
                  {downloading.excel ? 'Generating...' : 'Download Excel'}
                </Button>
                <Button
                  variant="outlined"
                  color="secondary"
                  startIcon={<PictureAsPdfIcon />}
                  onClick={handleDownloadPDF}
                  disabled={downloading.pdf}
                >
                  {downloading.pdf ? 'Generating...' : 'Download PDF'}
                </Button>
              </>
            )}
          </Box>
        </Box>
      </PageTitleWrapper>

      {/* STATS SECTION - Commented out */}
      {/* <Box sx={{ px: 2, pt: 0, pb: 2 }}>
        <Paper elevation={4} sx={{ p: 2, display: 'flex', flexWrap: 'wrap', gap: 3, justifyContent: 'space-between' }}>
          <Typography variant="subtitle1">Total Subscribers: <strong>{subscriberStats.total}</strong></Typography>
          <Typography variant="subtitle1">Active Subscribers: <strong>{subscriberStats.active}</strong></Typography>
          <Typography variant="subtitle1">Suspended Subscribers: <strong>{subscriberStats.suspended}</strong></Typography>
        </Paper>
      </Box> */}

      {/* FILTER SECTION */}
      <Box sx={{ px: 2 }}>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <Stack spacing={2} direction={{ xs: 'column', sm: 'row' }} alignItems="center">
            <TextField
              label="Search"
              variant="outlined"
              size="small"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{ minWidth: 200 }}
            />
            <DatePicker
              label="Start Date (DD/MM/YYYY)"
              value={startDate}
              onChange={(newValue) => setStartDate(newValue)}
              format="DD/MM/YYYY"
              slotProps={{ 
                textField: { 
                  size: 'small', 
                  sx: { minWidth: 180 },
                  placeholder: 'DD/MM/YYYY'
                } 
              }}
            />
            <DatePicker
              label="End Date (DD/MM/YYYY)"
              value={endDate}
              onChange={(newValue) => setEndDate(newValue)}
              format="DD/MM/YYYY"
              slotProps={{ 
                textField: { 
                  size: 'small', 
                  sx: { minWidth: 180 },
                  placeholder: 'DD/MM/YYYY'
                } 
              }}
            />
          </Stack>
        </LocalizationProvider>
      </Box>

      {/* DATAGRID SECTION */}
      <Box sx={{ height: 'calc(100vh - 280px)', width: '100%', padding: '15px' }}>
        <DataGrid
          rows={filteredRows}
          columns={columns}
          loading={loading}
          pagination
          pageSize={pageSize}
          onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
          rowsPerPageOptions={[5, 10, 20, 50, 100]}
          getRowHeight={() => 'auto'}
          sx={{
            boxShadow: 'none',
            border: 2,
            borderColor: 'rgb(43 44 45)',
            '& .MuiDataGrid-cell': {
              alignItems: 'start',
              paddingTop: '12px',
              paddingBottom: '12px',
            },
            '& .MuiDataGrid-cell:hover': {
              color: 'rgb(43 44 45)',
            },
          }}
        />
      </Box>
    </>
  );
};

export default SubscriberDetailReport;